/**
 * scoring-core — shared CMS decile mathematics.
 *
 * Extracted verbatim from mips-scoring.ts (P1, 2026-07). Consumed by BOTH the
 * MIPS quality scorer (mips-scoring.ts) and the APP Plus engine
 * (app-plus-scoring.ts, P3). This module is dependency-free by design: it
 * defines its own minimal benchmark shape rather than importing from
 * mips-data-service, so engines can feed it rows from any source.
 *
 * Any semantic change here changes TWO programs' scores. Do not edit without
 * running the full regression battery (mips-scoring.regression.test.ts).
 */

/** BINDING — minimal decile-bearing benchmark row. VbcaBenchmark satisfies this structurally. */
export interface DecileBenchmark {
  decile_1: string;
  decile_2: string;
  decile_3: string;
  decile_4: string;
  decile_5: string;
  decile_6: string;
  decile_7: string;
  decile_8: string;
  decile_9: string;
  decile_10: string;
}

/** BINDING signature. Parses "<= 30.00", "30.01 - 50.00", ">= 98.00", "100", "--". */
export function parseDecileRange(rangeStr: string): { min: number | null; max: number | null } {
  if (!rangeStr || rangeStr === '--') {
    return { min: null, max: null };
  }

  if (rangeStr.startsWith('<=')) {
    const value = parseFloat(rangeStr.substring(2).trim());
    return { min: 0, max: value };
  }

  if (rangeStr.startsWith('>=')) {
    const value = parseFloat(rangeStr.substring(2).trim());
    return { min: value, max: 100 };
  }

  if (rangeStr.includes(' - ')) {
    const [firstStr, secondStr] = rangeStr.split(' - ').map(s => s.trim());
    const first = parseFloat(firstStr);
    const second = parseFloat(secondStr);
    // Inverse-measure ranges may be written high-to-low (e.g., "50.00 - 40.01");
    // normalize so min is always the smaller value.
    return {
      min: Math.min(first, second),
      max: Math.max(first, second),
    };
  }

  const value = parseFloat(rangeStr);
  if (!isNaN(value)) {
    return { min: value, max: value };
  }

  return { min: null, max: null };
}

/** BINDING signature. Returns the 1-based decile a rate falls into, or null. */
export function findDecile(
  performanceRate: number,
  benchmark: DecileBenchmark,
  isInverse: boolean
): number | null {
  const decileKeys = [
    'decile_1', 'decile_2', 'decile_3', 'decile_4', 'decile_5',
    'decile_6', 'decile_7', 'decile_8', 'decile_9', 'decile_10',
  ] as const;

  for (let i = 0; i < decileKeys.length; i++) {
    const rangeStr = benchmark[decileKeys[i]];
    const { min, max } = parseDecileRange(rangeStr);

    if (min !== null && max !== null) {
      if (performanceRate >= min && performanceRate <= max) {
        return i + 1;
      }
    }
  }

  return null;
}

/** True when the rate is worse than the whole decile-1 band (no band below it). */
export function isBelowDecileOne(rate: number, benchmark: DecileBenchmark, isInverse: boolean): boolean {
  const d1 = parseDecileRange(benchmark.decile_1);
  if (d1.min === null || d1.max === null) return false;
  return isInverse ? rate > d1.max : rate < d1.min;
}

/**
 * Fail-low gap handling: when a 2dp-rounded rate still matches no band
 * (malformed benchmark row), return the highest decile whose band sits entirely
 * on the WORSE side of the rate — the score already beaten. Null when nothing
 * sits below.
 */
export function snapToLowerBand(rate: number, benchmark: DecileBenchmark, isInverse: boolean): number | null {
  let best: number | null = null;
  for (let d = 1; d <= 10; d++) {
    const { min, max } = parseDecileRange(benchmark[`decile_${d}` as keyof DecileBenchmark]);
    if (min === null || max === null) continue;
    const bandIsWorse = isInverse ? min > rate : max < rate;
    if (bandIsWorse) best = d; // deciles ascend in quality; keep the highest such band
  }
  return best;
}

/**
 * Decile lookup for a continuous rate. Benchmark bands are published to 2
 * decimals with 0.01 GAPS between them (e.g. "60.00 - 50.01" | "70.00 - 60.01"),
 * so the rate is rounded to 2dp before lookup — a rate inside a band always
 * rounds to a value inside that same band, so only gap rates are affected. A
 * residual gap (malformed row) snaps to the lower band; never fails high.
 * Returns null only when the rate is below decile 1 or no band parses.
 */
export function lookupDecile(
  rate: number,
  benchmark: DecileBenchmark,
  isInverse: boolean
): { decile: number | null; lookupRate: number } {
  const lookupRate = Math.round(rate * 100) / 100;
  let decile = findDecile(lookupRate, benchmark, isInverse);
  if (decile === null && !isBelowDecileOne(lookupRate, benchmark, isInverse)) {
    decile = snapToLowerBand(lookupRate, benchmark, isInverse);
  }
  return { decile, lookupRate };
}

/**
 * Tenth-of-a-point handling for the partial credit (P3, additive):
 * - 'round'    — today's MIPS behavior (default; byte-identical to pre-P1 code)
 * - 'truncate' — the APP Plus path (spec v14 Example H pins truncation)
 * Truncation of a sub-1 value naturally yields ≤ 0.9; the >0.9 clamp stays for
 * out-of-band rates (e.g. the APP engine's gap-snap feeds rates above the band).
 */
export interface PartialCreditOptions {
  partialMode?: 'round' | 'truncate';
}

/**
 * BINDING signature. CMS linear partial credit: X + ((q - a) / (b - a)),
 * partial to 1 decimal (rounded by default, truncated for APP — see
 * PartialCreditOptions), capped at 0.9; decile 10 earns no partial.
 * '--' in the current or next decile short-circuits to base points.
 */
export function calculatePointsFromDecile(
  decile: number,
  performanceRate: number,
  benchmark: DecileBenchmark,
  isInverse: boolean,
  hasScoringFloor: boolean,
  scoringFloor: string | undefined,
  hasSevenPointCap: boolean,
  options?: PartialCreditOptions
): number {
  let points = decile;

  if (decile < 10) {
    const currentDecileKey = `decile_${decile}` as keyof DecileBenchmark;
    const nextDecileKey = `decile_${decile + 1}` as keyof DecileBenchmark;

    const currentRangeStr = benchmark[currentDecileKey];
    const nextRangeStr = benchmark[nextDecileKey];

    if (currentRangeStr === '--' || nextRangeStr === '--') {
      return applyCapAndFloor(points, hasSevenPointCap, hasScoringFloor, scoringFloor);
    }

    const currentRange = parseDecileRange(currentRangeStr);
    const nextRange = parseDecileRange(nextRangeStr);

    if (currentRange.min !== null && nextRange.min !== null) {
      let a: number, b: number;

      if (isInverse) {
        // Inverse ranges run high→low: anchor on the range maxima; lower rate = more credit.
        a = currentRange.max !== null ? currentRange.max : currentRange.min;
        b = nextRange.max !== null ? nextRange.max : nextRange.min;

        if (a > b && performanceRate <= a) {
          const partialPoints = (a - performanceRate) / (a - b);

          let roundedPartial = options?.partialMode === 'truncate'
            ? Math.floor(partialPoints * 10) / 10
            : Math.round(partialPoints * 10) / 10;
          if (roundedPartial > 0.9) {
            roundedPartial = 0.9;
          }

          points = decile + roundedPartial;
        }
      } else {
        a = currentRange.min;
        b = nextRange.min;

        if (b > a && performanceRate >= a) {
          const partialPoints = (performanceRate - a) / (b - a);

          let roundedPartial = options?.partialMode === 'truncate'
            ? Math.floor(partialPoints * 10) / 10
            : Math.round(partialPoints * 10) / 10;
          if (roundedPartial > 0.9) {
            roundedPartial = 0.9;
          }

          points = decile + roundedPartial;
        }
      }
    }
  }

  return applyCapAndFloor(points, hasSevenPointCap, hasScoringFloor, scoringFloor);
}

/** BINDING signature. MIPS-specific modifiers (7-point cap; 5/7-point floors). */
export function applyCapAndFloor(
  points: number,
  hasSevenPointCap: boolean,
  hasScoringFloor: boolean,
  scoringFloor: string | undefined
): number {
  if (hasSevenPointCap && points > 7) {
    points = 7;
  }

  if (hasScoringFloor && scoringFloor) {
    if (scoringFloor === '7_Points' && points < 7) {
      points = 7;
    } else if (scoringFloor === '5_Points' && points < 5) {
      points = 5;
    }
  }

  return points;
}
